import { useEffect, useState } from 'react';

/**
 * Types matching the platform/architecture values used in the system
 */
type ValidPlatform = 'win' | 'osx' | 'linux';
type ValidPlatformArch = 'win-x64' | 'win-x86' | 'win-arm' | 'win-arm64' | 'osx-x64' | 'osx-arm64' | 'linux-generic-x64' | 'linux-generic-arm64';

interface SystemInfo {
  platform: ValidPlatform | null;
  architecture: ValidPlatformArch | null;
  userAgent: string;
}

/**
 * Hook to detect system information (platform and architecture) in browser environment
 */
const useSystemInfo = (): SystemInfo => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo>({
    platform: null,
    architecture: null,
    userAgent: '',
  });

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    let platform: ValidPlatform | null = null;
    let architecture: ValidPlatformArch | null = null;

    // Detect platform (OS)
    if (userAgent.indexOf('win') !== -1) {
      platform = 'win';
    } else if (userAgent.indexOf('mac') !== -1) {
      platform = 'osx';
    } else if (userAgent.indexOf('linux') !== -1) {
      platform = 'linux';
    }

    // Detect architecture
    // This is challenging in browsers as they don't reliably expose architecture information
    // We'll use heuristics based on available information

    if (platform === 'win') {
      if (userAgent.indexOf('win64') !== -1 || userAgent.indexOf('wow64') !== -1) {
        architecture = 'win-x64';
      } else if (userAgent.indexOf('arm') !== -1) {
        architecture = 'win-arm';
      } else {
        architecture = 'win-x86'; // Default to x86 for Windows if not detected as 64bit
      }
    } else if (platform === 'osx') {
      // For Mac, intel vs. ARM (Apple Silicon) detection is tricky in browsers
      // We can look for "mac os x" version and other clues
      if (userAgent.indexOf('mac os x') !== -1) {
        // The presence of "Intel" in the userAgent typically indicates Intel-based Macs
        if (userAgent.indexOf('intel') !== -1) {
          architecture = 'osx-x64';
        } else {
          // Newer Apple Silicon Macs tend to hide processor info in userAgent
          // Safari on M1/M2 shows nothing different in UA
          // Chrome on Apple Silicon has started adding "macOS on arm64" in some versions
          architecture = userAgent.indexOf('arm') !== -1 ? 'osx-arm64' : 'osx-x64';
        }
      }
    } else if (platform === 'linux') {
      // Browsers rarely expose Linux arch in userAgent; userAgentData may have it.
      // Distro (debian/rhel/arch) cannot be detected in browser, so we use generic.
      architecture = userAgent.indexOf('aarch64') !== -1 || userAgent.indexOf('arm64') !== -1
        ? 'linux-generic-arm64'
        : 'linux-generic-x64';
    }

    // Try to use the newer userAgentData API if available
    // @ts-expect-error not fully supported in all browsers
    if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
      // @ts-expect-error ignore
      navigator.userAgentData.getHighEntropyValues(['architecture', 'platformVersion'])
        .then((data: { architecture: string; platformVersion: string }) => {
          // Update architecture based on more reliable data if available
          if (platform === 'win' && data.architecture) {
            if (data.architecture === 'x86-64') {
              architecture = 'win-x64';
            } else if (data.architecture === 'x86') {
              architecture = 'win-x86';
            } else if (data.architecture.includes('arm')) {
              architecture = data.architecture.includes('64') ? 'win-arm64' : 'win-arm';
            }
          } else if (platform === 'osx' && data.architecture) {
            if (data.architecture.includes('arm')) {
              architecture = 'osx-arm64';
            } else {
              architecture = 'osx-x64';
            }
          } else if (platform === 'linux' && data.architecture) {
            architecture = data.architecture.includes('arm') ? 'linux-generic-arm64' : 'linux-generic-x64';
          }

          setSystemInfo({
            platform,
            architecture,
            userAgent,
          });
        })
        .catch(() => {
          // In case of error, we still have our best guess from userAgent
          setSystemInfo({
            platform,
            architecture,
            userAgent,
          });
        });
    } else {
      // If userAgentData API is not available, use our best guess
      setSystemInfo({
        platform,
        architecture,
        userAgent,
      });
    }
  }, []);

  return systemInfo;
};

export default useSystemInfo;
